import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Tag, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "all", label: "All Posts" },
  { value: "government_updates", label: "Government Updates" },
  { value: "programs", label: "Programs" },
  { value: "media_releases", label: "Media Releases" },
  { value: "fightfitash_updates", label: "Fight Fi Tash Updates" },
];

// Sample posts for initial display
const SAMPLE_POSTS = [
  {
    id: "1",
    title: "Welcome to The Kingdom of Yunah'Shach'Qal Yashar'al",
    slug: "welcome-to-the-kingdom",
    category: "government_updates",
    excerpt: "We are pleased to announce the official launch of our government portal. This marks a significant milestone in our nation-building journey.",
    content: "Full content here...",
    created_at: "2024-04-14T00:00:00Z",
  },
  {
    id: "2",
    title: "Fight Fi Tash Campaign Update",
    slug: "fight-fi-tash-update",
    category: "fightfitash_updates",
    excerpt: "Latest developments in our ongoing fight against state-sanctioned child abuse. SCOTUS has been notified.",
    content: "Full content here...",
    created_at: "2024-06-20T00:00:00Z",
  },
  {
    id: "3",
    title: "New Programs Open for Registration",
    slug: "new-programs-registration",
    category: "programs",
    excerpt: "Several new programs are now accepting applications including Research, Health, and Outreach/Missions ministries.",
    content: "Full content here...",
    created_at: "2024-05-01T00:00:00Z",
  },
  {
    id: "4",
    title: "New Music Release: Oathbreakers",
    slug: "new-music-oathbreakers",
    category: "media_releases",
    excerpt: "Shaba Sharah releases new track 'Oathbreakers' - a powerful declaration against Babylon's system.",
    content: "Full content here...",
    created_at: "2024-05-15T00:00:00Z",
  },
];

export default function NewsBlog() {
  const [posts, setPosts] = useState(SAMPLE_POSTS);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = activeCategory !== "all" ? { category: activeCategory } : {};
        const response = await axios.get(`${API}/posts`, { params });
        if (response.data.length > 0) {
          setPosts(response.data);
        }
      } catch (error) {
        console.log("Using sample posts");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [activeCategory]);

  const filteredPosts = activeCategory === "all" 
    ? posts 
    : posts.filter(post => post.category === activeCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCategoryLabel = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="min-h-screen" data-testid="news-blog-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
            News & Blog
          </span>
          <h1 className="heading-hero mt-2 mb-4">Latest Updates</h1>
          <p className="body-large text-slate-300 max-w-2xl">
            Stay informed with the latest news, announcements, and updates from 
            The Kingdom of Yunah'Shach'Qal Yashar'al.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-4">
            {CATEGORIES.map((category) => (
              <button
                key={category.value}
                onClick={() => setActiveCategory(category.value)}
                className={`px-4 py-2 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === category.value
                    ? "bg-kingdom-gold text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`category-${category.value}`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Loading posts...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No posts found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="card-official hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(post.category)}
                      </Badge>
                    </div>
                    <CardTitle className="heading-card text-royal-blue line-clamp-2">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 line-clamp-3 mb-4">
                      {post.excerpt}
                    </CardDescription>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      <button className="text-kingdom-gold hover:underline flex items-center gap-1">
                        Read More
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="bg-kingdom-gold py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            Never Miss an Update
          </h2>
          <p className="text-white/90 mb-6">
            Join our mailing list to receive the latest news and announcements.
          </p>
          <a href="mailto:admin@yasharal.vip">
            <button className="bg-royal-blue hover:bg-slate-800 text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider">
              Contact Us to Subscribe
            </button>
          </a>
        </div>
      </section>
    </div>
  );
}
